import React, { forwardRef, useEffect, useMemo } from "react";
import { TextArea } from "@streeteasy/design-system/form/TextAreaField";
import { Field, SelectInput, TextInput } from "design-system";
import { bool, func, node, oneOf, string } from "prop-types";
import uuid from "uuid/v4";

import InputWrapper from "../InputWrapper";
import ToggleGroup from "./ToggleGroup";

const InputMap = {
  select: SelectInput,
  text: TextInput,
  textarea: TextArea,
  toggle: ToggleGroup
};

const InputWithLabel = forwardRef(
  (
    {
      children,
      className,
      errorMessage,
      hint,
      id,
      labelText,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      required,
      type,
      value,
      ...props
    },
    ref
  ) => {
    const InputComponent = InputMap[type] || TextInput;
    const isValid = !errorMessage;
    const isSelectInput = type === "select";
    let inputHint = hint;

    if (required) {
      inputHint = hint ? `${hint} (required)` : "required";
    }

    useEffect(() => {
      if (!labelText && process.env.NODE_ENV !== "production") {
        console.warn(
          "As per a11y guidelines, please add a label to your form input"
        );
      }
    }, [labelText]);

    const fieldId = useMemo(() => id || uuid(), [id]);

    const fieldProps = {
      error: errorMessage,
      hint: inputHint,
      id: fieldId,
      isValid,
      label: labelText
    };
    const inputProps = {
      ...props,
      id: fieldId,
      isValid,
      name,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      ref,
      type,
      value: value || ""
    };
    const inputChildren = isSelectInput ? children : null;
    const wrapperChildren = !isSelectInput ? children : null;

    return (
      <InputWrapper className={className}>
        <Field {...fieldProps}>
          <InputComponent
            {...inputProps}
            className={inputProps.inputClassName}
            style={{ resize: "none", ...inputProps.style }}
          >
            {inputChildren}
          </InputComponent>
        </Field>
        {wrapperChildren}
      </InputWrapper>
    );
  }
);

InputWithLabel.displayName = "Input_LabelledInput_Inner";

InputWithLabel.propTypes = {
  children: node,
  className: string,
  errorMessage: string,
  hint: string,
  id: string,
  labelText: string.isRequired,
  name: string.isRequired,
  onBlur: func,
  onChange: func,
  onFocus: func,
  placeholder: string,
  required: bool,
  type: oneOf(["text", "textarea", "checkbox", "radio", "select", "toggle"]),
  value: string
};

InputWithLabel.defaultProps = {
  children: null,
  className: null,
  errorMessage: null,
  hint: null,
  id: null,
  onBlur: () => null,
  onChange: () => null,
  onFocus: () => null,
  placeholder: null,
  required: null,
  type: "text",
  value: ""
};

export default InputWithLabel;
